import React, { useEffect, useState } from "react";
import { Home, Search, Ticket, User, Plus, MapPin, CreditCard, Trash2, Pencil, X } from "lucide-react";
import PanelShell from "../../components/PanelShell";
import { useAuth } from "@/context/AuthContext";
import { clientApi } from "@/lib/clientApi";

const navItems = [
  { to: "/cliente", label: "Inicio", icon: Home, end: true },
  { to: "/cliente/buscar", label: "Buscar", icon: Search },
  { to: "/cliente/historial", label: "Reservas", icon: Ticket },
  { to: "/cliente/perfil", label: "Perfil", icon: User },
];
const bottomNav = [
  { to: "/cliente", label: "Inicio", icon: Home, end: true },
  { to: "/cliente/buscar", label: "Buscar", icon: Search },
  { to: "/cliente/reservar", label: "Reservar", icon: Plus },
  { to: "/cliente/historial", label: "Viajes", icon: Ticket },
  { to: "/cliente/perfil", label: "Perfil", icon: User },
];

const METHOD_LABELS = { yape: "Yape", tarjeta: "Tarjeta", transferencia: "Transferencia" };

const EMPTY_ADDRESS_FORM = { label: "", address: "", reference: "" };
const EMPTY_METHOD_FORM = { method_type: "yape", label: "" };

const ClientSavedPreferences = () => {
  const { user } = useAuth();

  const [addresses, setAddresses] = useState([]);
  const [addressForm, setAddressForm] = useState(EMPTY_ADDRESS_FORM);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);

  const [methods, setMethods] = useState([]);
  const [methodForm, setMethodForm] = useState(EMPTY_METHOD_FORM);
  const [editingMethodId, setEditingMethodId] = useState(null);
  const [showMethodForm, setShowMethodForm] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [addressesRes, methodsRes] = await Promise.all([clientApi.listAddresses(), clientApi.listPaymentMethods()]);
      setAddresses(addressesRes.data);
      setMethods(methodsRes.data);
    } catch {
      setError("No se pudieron cargar tus preferencias guardadas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // --- Direcciones ---

  const startEditAddress = (a) => {
    setEditingAddressId(a.id);
    setAddressForm({ label: a.label, address: a.address, reference: a.reference || "" });
    setShowAddressForm(true);
  };

  const startNewAddress = () => {
    setEditingAddressId(null);
    setAddressForm(EMPTY_ADDRESS_FORM);
    setShowAddressForm(true);
  };

  const submitAddress = async (e) => {
    e.preventDefault();
    setError("");
    const payload = { ...addressForm, reference: addressForm.reference || null };
    try {
      if (editingAddressId) {
        await clientApi.updateAddress(editingAddressId, payload);
      } else {
        await clientApi.createAddress(payload);
      }
      setShowAddressForm(false);
      setAddressForm(EMPTY_ADDRESS_FORM);
      setEditingAddressId(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo guardar la dirección.");
    }
  };

  const removeAddress = async (id) => {
    setError("");
    try {
      await clientApi.deleteAddress(id);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo borrar la dirección.");
    }
  };

  // --- Métodos de pago ---

  const startEditMethod = (m) => {
    setEditingMethodId(m.id);
    setMethodForm({ method_type: m.method_type, label: m.label });
    setShowMethodForm(true);
  };

  const startNewMethod = () => {
    setEditingMethodId(null);
    setMethodForm(EMPTY_METHOD_FORM);
    setShowMethodForm(true);
  };

  const submitMethod = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editingMethodId) {
        await clientApi.updatePaymentMethod(editingMethodId, methodForm);
      } else {
        await clientApi.createPaymentMethod(methodForm);
      }
      setShowMethodForm(false);
      setMethodForm(EMPTY_METHOD_FORM);
      setEditingMethodId(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo guardar el método de pago.");
    }
  };

  const removeMethod = async (id) => {
    setError("");
    try {
      await clientApi.deletePaymentMethod(id);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo borrar el método de pago.");
    }
  };

  return (
    <PanelShell role="Cliente" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} testId="client-saved-preferences">
      <div className="max-w-4xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Perfil</p>
        <h1 className="font-display font-bold text-3xl uppercase tracking-tight mt-1">Preferencias guardadas</h1>

        {error && <p className="mt-4 text-xs text-red-400 font-body" data-testid="preferences-error">{error}</p>}

        {/* Direcciones */}
        <div className="mt-8 bg-[#1C1D20] border border-[#2E2F33] rounded-xl p-6 lg:p-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg uppercase tracking-wide flex items-center gap-2">
              <MapPin size={18} className="text-[#C9A227]" /> Direcciones guardadas
            </h2>
            <button
              onClick={startNewAddress}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#C9A227] hover:bg-[#D4AF37] text-[#0D0E10] font-display font-bold uppercase tracking-wider text-[10px] rounded-lg"
              data-testid="new-address-btn"
            >
              <Plus size={12} /> Nueva
            </button>
          </div>

          {showAddressForm && (
            <form onSubmit={submitAddress} className="mb-4 p-4 bg-[#0D0E10] border border-[#2E2F33] rounded-lg space-y-3" data-testid="address-form">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-[#C9A227] font-display">
                  {editingAddressId ? "Editar dirección" : "Nueva dirección"}
                </p>
                <button type="button" onClick={() => setShowAddressForm(false)} className="text-[#A0A0A5] hover:text-[#C9A227]">
                  <X size={14} />
                </button>
              </div>
              <input
                required
                value={addressForm.label}
                onChange={(e) => setAddressForm((f) => ({ ...f, label: e.target.value }))}
                placeholder="Etiqueta (ej. Casa, Oficina)"
                maxLength={50}
                className="w-full bg-[#1C1D20] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-3 py-2 text-sm text-[#F5F5F5] font-body outline-none"
                data-testid="address-label-input"
              />
              <input
                required
                value={addressForm.address}
                onChange={(e) => setAddressForm((f) => ({ ...f, address: e.target.value }))}
                placeholder="Dirección completa"
                className="w-full bg-[#1C1D20] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-3 py-2 text-sm text-[#F5F5F5] font-body outline-none"
                data-testid="address-text-input"
              />
              <input
                value={addressForm.reference}
                onChange={(e) => setAddressForm((f) => ({ ...f, reference: e.target.value }))}
                placeholder="Referencia (opcional)"
                className="w-full bg-[#1C1D20] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-3 py-2 text-sm text-[#F5F5F5] font-body outline-none"
                data-testid="address-reference-input"
              />
              <button type="submit" className="px-5 py-2 bg-[#C9A227] hover:bg-[#D4AF37] text-[#0D0E10] font-display font-bold uppercase tracking-wider text-xs rounded-lg" data-testid="save-address-btn">
                Guardar
              </button>
            </form>
          )}

          <div className="space-y-2">
            {!loading && addresses.length === 0 && <p className="text-xs text-[#A0A0A5] font-body">No tienes direcciones guardadas todavía.</p>}
            {addresses.map((a) => (
              <div key={a.id} className="flex items-center gap-3 p-3 bg-[#0D0E10] border border-[#2E2F33] rounded-lg" data-testid={`saved-address-${a.id}`}>
                <MapPin size={14} className="text-[#C9A227] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-wider text-[#C9A227] font-display">{a.label}</p>
                  <p className="text-sm text-[#F5F5F5] font-body truncate">{a.address}</p>
                </div>
                <button onClick={() => startEditAddress(a)} className="p-1.5 text-[#A0A0A5] hover:text-[#C9A227]" data-testid={`edit-address-${a.id}`}>
                  <Pencil size={13} />
                </button>
                <button onClick={() => removeAddress(a.id)} className="p-1.5 text-[#A0A0A5] hover:text-red-400" data-testid={`delete-address-${a.id}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Métodos de pago */}
        <div className="mt-6 bg-[#1C1D20] border border-[#2E2F33] rounded-xl p-6 lg:p-8">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-display font-bold text-lg uppercase tracking-wide flex items-center gap-2">
              <CreditCard size={18} className="text-[#C9A227]" /> Métodos de pago
            </h2>
            <button
              onClick={startNewMethod}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#C9A227] hover:bg-[#D4AF37] text-[#0D0E10] font-display font-bold uppercase tracking-wider text-[10px] rounded-lg"
              data-testid="new-method-btn"
            >
              <Plus size={12} /> Nuevo
            </button>
          </div>
          <p className="text-xs text-[#A0A0A5] font-body mb-4">
            Solo para tu referencia al reservar -- no procesamos pagos con tarjeta todavía.
          </p>

          {showMethodForm && (
            <form onSubmit={submitMethod} className="mb-4 p-4 bg-[#0D0E10] border border-[#2E2F33] rounded-lg space-y-3" data-testid="method-form">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-[#C9A227] font-display">
                  {editingMethodId ? "Editar método" : "Nuevo método"}
                </p>
                <button type="button" onClick={() => setShowMethodForm(false)} className="text-[#A0A0A5] hover:text-[#C9A227]">
                  <X size={14} />
                </button>
              </div>
              <select
                value={methodForm.method_type}
                onChange={(e) => setMethodForm((f) => ({ ...f, method_type: e.target.value }))}
                className="w-full bg-[#1C1D20] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-3 py-2 text-sm text-[#F5F5F5] font-body outline-none"
                data-testid="method-type-select"
              >
                <option value="yape">Yape</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="transferencia">Transferencia</option>
              </select>
              <input
                required
                value={methodForm.label}
                onChange={(e) => setMethodForm((f) => ({ ...f, label: e.target.value }))}
                placeholder='Etiqueta (ej. "Yape terminado en 1234")'
                maxLength={100}
                className="w-full bg-[#1C1D20] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-3 py-2 text-sm text-[#F5F5F5] font-body outline-none"
                data-testid="method-label-input"
              />
              <button type="submit" className="px-5 py-2 bg-[#C9A227] hover:bg-[#D4AF37] text-[#0D0E10] font-display font-bold uppercase tracking-wider text-xs rounded-lg" data-testid="save-method-btn">
                Guardar
              </button>
            </form>
          )}

          <div className="space-y-2">
            {!loading && methods.length === 0 && <p className="text-xs text-[#A0A0A5] font-body">No tienes métodos de pago guardados todavía.</p>}
            {methods.map((m) => (
              <div key={m.id} className="flex items-center gap-3 p-3 bg-[#0D0E10] border border-[#2E2F33] rounded-lg" data-testid={`saved-method-${m.id}`}>
                <CreditCard size={14} className="text-[#C9A227] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-wider text-[#C9A227] font-display">{METHOD_LABELS[m.method_type]}</p>
                  <p className="text-sm text-[#F5F5F5] font-body truncate">{m.label}</p>
                </div>
                <button onClick={() => startEditMethod(m)} className="p-1.5 text-[#A0A0A5] hover:text-[#C9A227]" data-testid={`edit-method-${m.id}`}>
                  <Pencil size={13} />
                </button>
                <button onClick={() => removeMethod(m.id)} className="p-1.5 text-[#A0A0A5] hover:text-red-400" data-testid={`delete-method-${m.id}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PanelShell>
  );
};

export default ClientSavedPreferences;
